import {
  Box,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  SimpleGrid,
  Text,
} from '@chakra-ui/react';
import Button from '~/components/Button';
import {
  METRICS,
  METRICS_NAME,
  MONTH_NAME_DAY_YEAR_FORMAT,
  SOCIAL_PLATFORMS_CONNECTION_STATUS,
} from '~/constants';
import {
  CreatorBenchmarks,
  LastUpdatedKeysTypes,
  MediaPackBenchmarkType,
} from '~/types';
import { formatDate } from '~/utils';
import { useBreakpointValue } from '~/utils-ts';

import {
  IMetric,
  IMetricConfig,
} from './MediaPackDetailEditHighlightsModal.types';
import { TValidateErrors } from './MediaPackDetailEditHighlightsModal.validators';
import { CheckboxCard, MetricCard } from './components';

interface Props {
  benchmarks: CreatorBenchmarks;
  errors: TValidateErrors;
  metricActive: keyof typeof METRICS;
  metrics: IMetric[];
  metricsConfig: { [K in METRICS]: IMetricConfig } & {
    lastUpdatedKey: LastUpdatedKeysTypes;
  };
  metricsPosition: { [k in METRICS]: MediaPackBenchmarkType[] };
  onChangeBenchmark: (benchmark: MediaPackBenchmarkType) => void;
  onClickMetric: (metric: METRICS) => void;
  platformConnection?: string;
}

export const MediaPackDetailEditHighlightsModalComponent: React.FC<Props> = ({
  benchmarks,
  errors,
  metricActive,
  metrics,
  metricsConfig,
  metricsPosition,
  onChangeBenchmark,
  onClickMetric,
  platformConnection,
}) => {
  const isMobile = useBreakpointValue({ base: true, lg: false });
  const currentMetricKeys = metricsPosition[metricActive];
  const lastUpdatedPlatform =
    platformConnection === SOCIAL_PLATFORMS_CONNECTION_STATUS.connectionExpired
      ? formatDate(
          benchmarks[metricsConfig.lastUpdatedKey as LastUpdatedKeysTypes],
          MONTH_NAME_DAY_YEAR_FORMAT,
        )
      : undefined;
  const otherMetricKeys = Object.entries(metricsPosition).reduce(
    (acc: MediaPackBenchmarkType[], [metric, values]) => {
      if (metric !== metricActive) {
        acc = [...acc, ...values];
      }
      return acc;
    },
    [],
  );
  return (
    <Box>
      {!isMobile && (
        <Text fontSize="md" fontWeight={500} marginBottom={4}>
          Metric displayed:
        </Text>
      )}

      {isMobile && (
        <>
          <Flex marginBottom={4}>
            {Object.values(METRICS).map((metric) => (
              <Button
                _active={{
                  color: 'primary.500',
                  textDecoration: 'none',
                  '::before': {
                    position: 'absolute',
                    top: '100%',
                    left: '0',
                    width: '100%',
                    height: '3px',
                    backgroundColor: 'secondary.500',
                    transform: 'translateY(6px)',
                    content: '""',
                  },
                }}
                key={`button-${metric}`}
                _notLast={{ marginRight: 14 }}
                color="primary.300"
                display="block"
                fontSize="md"
                fontWeight={500}
                isActive={metric === metricActive}
                marginBottom={3}
                onClick={() => onClickMetric(metric)}
                position="relative"
                textAlign="left"
                variant="link"
                rounded="none"
              >
                {METRICS_NAME[metric]}
              </Button>
            ))}
          </Flex>

          <Text
            color="primary.500"
            fontSize="md"
            fontWeight={500}
            marginBottom={4}
          >
            Metric displayed:
          </Text>
        </>
      )}

      <SimpleGrid columns={{ base: 1, lg: 3 }} columnGap={6} marginBottom={10}>
        {Object.entries(metricsConfig)
          .filter(([metric]) => metric !== 'lastUpdatedKey')
          .filter(([metric]) => (isMobile ? metric === metricActive : true))
          .map(([metric, metricConfig]) => (
            <MetricCard
              benchmarks={benchmarks}
              isActive={metricActive === metric}
              key={`card-${metric}`}
              metric={metric as METRICS}
              metricConfig={metricConfig as IMetricConfig}
              metricPosition={metricsPosition[metric as METRICS]}
              metrics={metrics}
              onClick={isMobile ? undefined : onClickMetric}
              lastUpdated={lastUpdatedPlatform}
            />
          ))}
      </SimpleGrid>

      <Text fontSize="lg" fontWeight={500}>
        Alternatives for <b>{METRICS_NAME[metricActive]}</b>:
      </Text>

      <Text fontSize="md" marginBottom={4}>
        *Data marked as N/A will not be displayed into the media pack
      </Text>

      {isMobile && <Divider />}

      <Box
        overflow="hidden"
        overflowY="auto"
        maxHeight={isMobile ? 'calc(100vh - 514px)' : undefined}
      >
        <SimpleGrid
          columns={{ base: 1, md: 2, lg: 3 }}
          columnGap={{ base: 4, md: 6 }}
          rowGap={{ base: 4, md: 6 }}
          paddingTop={{ base: 4, md: 6, lg: 0 }}
          paddingBottom={{ base: 4, md: 6 }}
        >
          {Object.values(metrics).map(
            ({ key, title, description, transformValue }) => {
              const value = benchmarks[key];

              if (typeof value === 'undefined') return null;

              return (
                <CheckboxCard
                  description={description}
                  isMultiselect={metricsConfig[metricActive].isMultiselect}
                  key={key}
                  metricKey={key}
                  metricKeys={currentMetricKeys}
                  onChange={() => onChangeBenchmark(key)}
                  otherMetricKey={otherMetricKeys}
                  title={title}
                  transformValue={transformValue}
                  value={value}
                  lastUpdated={lastUpdatedPlatform}
                />
              );
            },
          )}
        </SimpleGrid>

        <FormControl isInvalid={!!errors[metricActive]}>
          <FormErrorMessage
            data-testid={`error-${metricActive}`}
            marginBottom={{ base: 4, md: 6 }}
          >
            {errors[metricActive]}
          </FormErrorMessage>
        </FormControl>
      </Box>
    </Box>
  );
};
